<script setup lang="ts">
import { computed, ref } from 'vue';
import { useAudioPlayer } from '@/composables/useAudioPlayer';

interface Props {
  layout?: 'inline' | 'stacked';
  showTimeLabels?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  color?: string;
  trackColor?: string;
}

const props = withDefaults(defineProps<Props>(), {
  layout: 'inline',
  showTimeLabels: true,
  disabled: false,
  readonly: false,
  color: 'primary',
  trackColor: 'surface-variant',
});

const emit = defineEmits<{
  (e: 'seek', value: number): void;
  (e: 'seek-start'): void;
  (e: 'seek-end'): void;
}>();

const audioPlayer = useAudioPlayer();
const isSeeking = ref(false);

const currentTime = computed(() => audioPlayer.currentTime.value);
const duration = computed(() => audioPlayer.duration.value);

const progressPercent = computed(() => {
  if (!duration.value || duration.value <= 0) return 0;
  const percent = (currentTime.value / duration.value) * 100;
  return Math.max(0, Math.min(100, percent));
});

function formatTime(seconds: number): string {
  if (!seconds || !isFinite(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function onSeekStart() {
  isSeeking.value = true;
  emit('seek-start');
}

function onSeekEnd() {
  isSeeking.value = false;
  emit('seek-end');
}

function onSeek(value: number | number[]) {
  if (props.disabled || props.readonly) return;

  const percent = Array.isArray(value) ? value[0] : value;
  const seekTime = (percent / 100) * duration.value;
  audioPlayer.seek(seekTime);
  emit('seek', seekTime);
}

// For stacked layout: seek using direct time value
function onSeekDirect(value: number | number[]) {
  if (props.disabled || props.readonly) return;

  const seekTime = Array.isArray(value) ? value[0] : value;
  audioPlayer.seek(seekTime);
  emit('seek', seekTime);
}
</script>

<template>
  <div
    class="progress-bar"
    :class="[
      `progress-bar--${layout}`,
      { 'progress-bar--disabled': disabled || readonly }
    ]"
  >
    <!-- Stacked Layout: times above slider -->
    <template v-if="layout === 'stacked'">
      <div v-if="showTimeLabels" class="progress-bar__time-row">
        <span class="progress-bar__time">{{ formatTime(currentTime) }}</span>
        <span class="progress-bar__time">{{ formatTime(duration) }}</span>
      </div>
      <v-slider
        :model-value="currentTime"
        :max="duration || 100"
        :step="0.1"
        :disabled="disabled"
        :readonly="readonly"
        hide-details
        :color="color"
        track-color="rgba(255,255,255,0.3)"
        thumb-color="white"
        class="progress-bar__slider progress-bar__slider--stacked"
        @start="onSeekStart"
        @end="onSeekEnd"
        @update:model-value="onSeekDirect"
      />
    </template>

    <!-- Inline Layout: time-slider-time -->
    <template v-else>
      <span v-if="showTimeLabels" class="progress-bar__time">
        {{ formatTime(currentTime) }}
      </span>
      <v-slider
        :model-value="progressPercent"
        :max="100"
        :step="0.1"
        :disabled="disabled"
        :readonly="readonly"
        hide-details
        density="compact"
        :color="color"
        :track-color="trackColor"
        class="progress-bar__slider"
        @start="onSeekStart"
        @end="onSeekEnd"
        @update:model-value="onSeek"
      />
      <span v-if="showTimeLabels" class="progress-bar__time">
        {{ formatTime(duration) }}
      </span>
    </template>
  </div>
</template>

<style scoped lang="scss">
.progress-bar {
  display: flex;
  align-items: center;
  width: 100%;

  &--inline {
    flex-direction: row;
    gap: 8px;
  }

  &--stacked {
    flex-direction: column;
    gap: 8px;

    .progress-bar__slider {
      width: 100%;
    }
  }

  &--disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &__time-row {
    display: flex;
    justify-content: space-between;
    width: 100%;
  }

  &__time {
    font-size: 0.75rem;
    color: rgba(var(--v-theme-on-surface), 0.6);
    min-width: 36px;
    text-align: center;
  }

  &__slider {
    flex: 1;
    min-width: 0;

    // Override Vuetify slider defaults for compact, centered layout
    :deep(.v-input.v-input--horizontal) {
      margin-inline: 0;
    }

    :deep(.v-input__control),
    :deep(.v-slider__container) {
      min-height: 20px;
      display: flex;
      align-items: center;
    }

    :deep(.v-slider-track),
    :deep(.v-slider-track__background),
    :deep(.v-slider-track__fill) {
      height: 4px;
    }

    :deep(.v-slider-track) {
      --v-slider-track-size: 4px;
    }

    :deep(.v-slider-thumb) {
      --v-slider-thumb-size: 12px;
    }

    :deep(.v-slider-thumb__surface) {
      width: 12px;
      height: 12px;
    }
  }

  // Stacked layout specific styles
  &--stacked &__time {
    color: rgba(255, 255, 255, 0.6);
  }

  &--stacked &__slider {
    :deep(.v-slider-thumb) {
      width: 16px;
      height: 16px;
    }
  }

  // Ensure stacked slider track fill is visible
  &__slider--stacked {
    width: 100%;

    :deep(.v-slider-track) {
      height: 4px;
    }

    :deep(.v-slider-track__fill) {
      background-color: rgb(var(--v-theme-primary)) !important;
      height: 4px;
    }

    :deep(.v-slider-track__background) {
      background-color: rgba(255, 255, 255, 0.3) !important;
      height: 4px;
    }

    :deep(.v-slider-thumb__surface) {
      background-color: rgb(var(--v-theme-primary)) !important;
    }
  }
}
</style>
